import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAllSessions } from '../utils/sessionManager';

/**
 * InsightsView - Premium view for captured insights, notes, and learnings
 * Replaces the Frameworks tab with actionable, user-centered content
 */
const InsightsView = ({
  companyIdSlug,
  capturedInsights = [],
  savedNotes = [],
  onCreateNote
}) => {
  const [allSessions, setAllSessions] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all'); // all, insights, notes, goals
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    if (companyIdSlug) {
      const { sessions } = getAllSessions(companyIdSlug);
      setAllSessions(sessions || []);
    }
  }, [companyIdSlug]);

  // Aggregate all insights from sessions
  const allInsights = allSessions.reduce((acc, session) => {
    if (session.insights && session.insights.length > 0) {
      return [...acc, ...session.insights.map(insight => ({
        ...insight,
        sessionId: session.id,
        sessionTitle: session.title,
        timestamp: session.updatedAt || session.createdAt
      }))];
    }
    return acc;
  }, [...capturedInsights]);

  // Group insights by type
  const insightsByType = allInsights.reduce((acc, insight) => {
    const type = insight.type || 'General';
    if (!acc[type]) acc[type] = [];
    acc[type].push(insight);
    return acc;
  }, {});

  // Get goals from insights
  const goals = allInsights.filter(i =>
    i.type === 'Goal' || i.type === 'Objective' || (i.label && i.label.toLowerCase().includes('goal'))
  );

  // Filter content based on active filter
  const getFilteredContent = () => {
    switch (activeFilter) {
      case 'insights':
        return allInsights.filter(i => i.type !== 'Goal' && i.type !== 'Objective');
      case 'notes':
        return savedNotes;
      case 'goals':
        return goals;
      default:
        return [...allInsights, ...savedNotes];
    }
  };

  const filteredContent = getFilteredContent();

  // Format date helper
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get icon for insight type
  const getInsightIcon = (type) => {
    const iconMap = {
      'Goal': '🎯',
      'Objective': '📌',
      'Insight': '💡',
      'Learning': '📚',
      'Action': '✅',
      'Idea': '🌟',
      'Question': '❓',
      'Note': '📝',
      'Default': '💭'
    };
    return iconMap[type] || iconMap['Default'];
  };

  // Empty state
  if (allInsights.length === 0 && savedNotes.length === 0) {
    return (
      <div className="mm-insights-premium">
        <div className="mm-view-header">
          <h2 className="mm-view-title">Your Insights</h2>
          <p className="mm-view-count">Track your progress and learnings</p>
        </div>

        <div className="mm-empty-state">
          <div className="mm-empty-icon">💡</div>
          <h3 className="mm-empty-title">No insights yet</h3>
          <p className="mm-empty-description">
            As you have coaching conversations, key insights, goals, and learnings will appear here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mm-insights-premium">
      {/* Header */}
      <div className="mm-view-header">
        <h2 className="mm-view-title">Your Insights</h2>
        <p className="mm-view-count">
          {allInsights.length} insight{allInsights.length !== 1 ? 's' : ''} • {savedNotes.length} note{savedNotes.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="mm-filter-tabs">
        <button
          className={`mm-filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          All
        </button>
        <button
          className={`mm-filter-tab ${activeFilter === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveFilter('insights')}
        >
          Insights
        </button>
        <button
          className={`mm-filter-tab ${activeFilter === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveFilter('notes')}
        >
          Notes
        </button>
        {goals.length > 0 && (
          <button
            className={`mm-filter-tab ${activeFilter === 'goals' ? 'active' : ''}`}
            onClick={() => setActiveFilter('goals')}
          >
            Goals
          </button>
        )}
      </div>

      {/* Summary Stats */}
      {activeFilter === 'all' && (
        <div className="mm-insights-summary">
          {Object.keys(insightsByType).length > 0 && (
            <div className="mm-summary-grid">
              {Object.entries(insightsByType).slice(0, 4).map(([type, items]) => (
                <motion.div
                  key={type}
                  className="mm-summary-card"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="mm-summary-icon">{getInsightIcon(type)}</div>
                  <div className="mm-summary-count">{items.length}</div>
                  <div className="mm-summary-label">{type}{items.length !== 1 ? 's' : ''}</div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content List */}
      <div className="mm-insights-content">
        <AnimatePresence mode="wait">
          {filteredContent.length === 0 ? (
            <motion.div
              key="empty"
              className="mm-empty-state"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="mm-empty-icon">🔍</div>
              <h3 className="mm-empty-title">No {activeFilter} found</h3>
              <p className="mm-empty-description">Try selecting a different filter</p>
            </motion.div>
          ) : (
            <div className="mm-insights-list">
              {filteredContent.map((item, index) => {
                const isNote = item.content || item.text;
                const isExpanded = expandedItem === `${item.id || item.sessionId}-${index}`;

                return (
                  <motion.div
                    key={`${item.id || item.sessionId}-${index}`}
                    className={`mm-insight-card ${isNote ? 'mm-note-variant' : ''} ${isExpanded ? 'expanded' : ''}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    onClick={() => setExpandedItem(isExpanded ? null : `${item.id || item.sessionId}-${index}`)}
                  >
                    <div className="mm-insight-icon-wrapper">
                      {getInsightIcon(item.type || (isNote ? 'Note' : 'Insight'))}
                    </div>
                    <div className="mm-insight-content">
                      <div className="mm-insight-type">
                        {item.type || (isNote ? 'Note' : 'Insight')}
                      </div>
                      <div className="mm-insight-value">
                        {item.value || item.label || item.content || item.text || 'N/A'}
                      </div>
                      {item.sessionTitle && (
                        <div className="mm-insight-source">
                          From: {item.sessionTitle}
                        </div>
                      )}
                      {item.timestamp && (
                        <div className="mm-insight-date">
                          {formatDate(item.timestamp)}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Create Note Button (Optional) */}
      {onCreateNote && (
        <motion.button
          className="mm-cta-voice mm-create-note-btn"
          onClick={onCreateNote}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="mm-cta-content">
            <span className="mm-cta-label">Create New Note</span>
            <span className="mm-cta-description">Capture a quick thought or learning</span>
          </div>
          <div className="mm-cta-icon">+</div>
        </motion.button>
      )}
    </div>
  );
};

export default InsightsView;
